import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { LMListSelectionEvent } from '@list/lm-list.component';
import {
  LMPagePaneListComponent,
  LMPaneListSelectionEvent,
  LMPaneListSendContextMessageEvent,
  LMPaneRowActivatedEvent
} from './lm-page-pane-list.component';
import { LMPagePaneDashBoardComponent } from './lm-page-pane-dashboard.component';
import { LMPagePanelHelperService } from './lm-page-panel-helper.service';
import { Observable, of as observableOf } from 'rxjs';
import { LMListSpecUtilService } from '@list/services/lm-list-spec-util.service';
import { LMContextService } from '@services/lm-context.service';
import { DomSanitizer } from '@angular/platform-browser';
import { LMPanelTransactionEvent, LMRefreshRequest } from '@app/components';

@Component({
  selector:    'lm-page-panel',
  templateUrl: './lm-page-panel.component.html',
  styleUrls:   [ 'lm-page-panel.component.scss' ],
  providers:   [ LMPagePanelHelperService ],
})
export class LMPagePanelComponent implements OnInit, AfterViewInit {

  @HostBinding('class.lm-page-multi-pane-panel') get classMultiPanePanel() {
    return this.isMultiPanePanel();
  }

  @HostBinding('class.lm-page-single-pane-panel') get classSinglePanePanel() {
    return !this.isMultiPanePanel();
  }

  @HostBinding('style.position') get position() {
    return 'relative';
  }

  @HostBinding('style.overflow') get overflow() {
    return this.pageHasContextForm ? '' : 'auto';
  }

  @HostBinding('class.is-vertical-split') get isVerticalSplitClass() {
    return this.isVerticalSplit;
  }

  // ---------------------------------------------------
  // INPUTS
  // ---------------------------------------------------
  @Input() panel: LMPagePanelSpec;
  @Input() needsReload: boolean;
  @Input() messageRuleView: LMDataView;
  @Input() overrideListTitle: string;
  @Input() insideTabs = false;
  @Input() isSinglePanePage = false;
  @Input() pageHasContextForm = false;
  @Input() pageHasTabs = false;

  /**
   * Whether or not this tabs has been visited. Set this to true
   * when a tab is selected to avoid building content until it comes
   * into view.
   */
  @Input() set activated(activated: boolean) {
    // activate panel to display it in the template
    this.panelActivated = activated;
    this.sendBusinessContext();

    if (this.isMultiPanePanel()) {
      // -------------------------------------------------------------------------------------
      // need to call this in a secondary timeout that takes place in 40ms. Starting the busy
      // indicator takes 20ms by default so any code should be executed after that otherwise
      // angular will block the busy indicator.
      // -------------------------------------------------------------------------------------
      setTimeout(() => this.buildDependentListMap(), 40);
    }
  }

  get activated(): boolean {
    return this._activated;
  }

  // ---------------------------------------------------
  // OUTPUTS
  // ---------------------------------------------------
  @Output() rowsSelected: EventEmitter<LMListSelectionEvent> = new EventEmitter<LMListSelectionEvent>();
  @Output() panelContentDisplayed = new EventEmitter<LMContentDisplayedEvent>();
  @Output() updateToolbarStateEvent = new EventEmitter<any>();
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() panelContentModified = new EventEmitter<LMListFieldChangedEvent>();
  @Output() panelDataUpdated = new EventEmitter<any>();
  @Output() contentModified = new EventEmitter<any>();

  // ---------------------------------------------------
  // CHILDREN VARIABLES
  // ---------------------------------------------------
  @ViewChildren(LMPagePaneListComponent) pagePaneListComponents: QueryList<LMPagePaneListComponent>;
  @ViewChildren(LMPagePaneDashBoardComponent) pagePaneDashBoardComponents: QueryList<LMPagePaneDashBoardComponent>;

  // ---------------------------------------------------
  // MEMBER VARIABLES
  // ---------------------------------------------------
  public orderedUIPanes: Array<LMUIMultiPagePane> = [];
  public onMobile = false;
  public isVerticalSplit: boolean;
  public multiPanePanel = false;
  public dashboardPanePanel = false;
  public menuPanel = false;
  public singleUrlPanePanel = false;
  public singlePanePanel = false;
  public multiListPanel = false;
  public singlePane: LMPagePaneSpec;
  public panelActivated = false;

  private orderedPanes: Array<LMMultiPagePaneSpec>;
  /** array of paneIds */
  private undisplayedPaneIdList: Array<string>;
  private _activated = false;
  private pagePaneMap: Map<string, LMPagePaneListComponent> = new Map<string, LMPagePaneListComponent>();

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private lmPagePanelHelperService: LMPagePanelHelperService,
    private lmListSpecUtilService: LMListSpecUtilService,
    private lmContextService: LMContextService,
    private domSanitizer: DomSanitizer
  ) {
    this.onMobile = this.lmContextService.isMobileDevice();
  }

  // ---------------------------------------------------
  // LIFECYCLE METHODS
  // ---------------------------------------------------
  ngOnInit() {
    this.checkIfVerticalSplit();
    this.buildHTMLVariables();
    if (this.singlePanePanel && !this.menuPanel && !this.singleUrlPanePanel && !this.dashboardPanePanel) {
      this.singlePane = this.singlePanelPane;
    }
  }

  ngAfterViewInit() {
    if (this.isMultiPanePanel()) {
      /**
       * Reorder the panes so that they are built into the container in the order
       * of the cascading dependent lists. In that was once the container forces
       * the panels to be responsive and line up in a single column so that the
       * dependent panel are in order.
       * @type {Array<LMMultiPagePaneSpec>}
       */
      this.orderedPanes = this.lmPagePanelHelperService.reorderDependentPanes(this.panel);
      this.undisplayedPaneIdList = this.orderedPanes.map(pane => pane.paneId);
      this.buildPanesHeightClasses();
    }
  }

  // ---------------------------------------------------
  // PUBLIC METHODS
  // ---------------------------------------------------
  public refresh(request?: LMRefreshRequest): Observable<any> {
    // -----------------------------------------------------------------------------------
    // if no lists anywhere then return false for refreshed. That way the toolbar won't
    // try and update.
    // -----------------------------------------------------------------------------------
    const pagePaneComponentArray = this.getPagePaneListComponents(request);
    if (pagePaneComponentArray.length === 0) {
      return observableOf(true); // safety
    }

    // -----------------------------------------------------------------------------------
    // if only one list on this panel then return the refresh() Observable.
    // -----------------------------------------------------------------------------------
    if (pagePaneComponentArray.length === 1) {
      // console.log('pagepanel 1 list refresh');
      return pagePaneComponentArray[ 0 ].refresh(request);
    }

    // -----------------------------------------------------------------------------------
    // if multiple lists on this panel then go through each list and
    // call refresh on it. Once all these lists have been refreshed then notify the
    // caller of this function through it's returned Observable.
    // -----------------------------------------------------------------------------------
    return new Observable<any>(observer => {
      const refreshedArray: boolean[] = Array(pagePaneComponentArray.length).fill(false);

      pagePaneComponentArray.forEach((pagePaneComponent, index) => {
        // console.log('pagepanel list refresh');
        pagePaneComponent.refresh(request).subscribe(refreshed => {     // DPB need to test this path must be a mult-pane  also use the forkJoin and do i need to refresh dependent list panes
          if (!refreshed) {
            observer.next(refreshed);
          }

          refreshedArray[ index ] = refreshed;
          if (refreshedArray.filter(refreshedElement => refreshedElement).length === refreshedArray.length) {
            observer.next(refreshed);
          }
        });
      });
    });
  }

  public isModified(): boolean {
    if (this.pagePaneListComponents.some(pagePaneListComponent => pagePaneListComponent.isModified())) {
      return true;
    }
    return this.pagePaneDashBoardComponents.some(pagePaneDashBoardComponent => pagePaneDashBoardComponent.isModified());

  }

  public save(): Observable<boolean> {
    // -----------------------------------------------------------------------------------
    // if no lists anywhere then return false for saved. That way the toolbar won't
    // try and update.
    // -----------------------------------------------------------------------------------
    const pagePaneComponentArray = this.getPagePaneListComponents();
    if (pagePaneComponentArray.length === 0) {
      return observableOf(false); // safety
    }

    // -----------------------------------------------------------------------------------
    // if only one list on this panel then return the save() Observable.
    // -----------------------------------------------------------------------------------
    if (pagePaneComponentArray.length === 1) {
      return pagePaneComponentArray[ 0 ].save();
    }

    // -----------------------------------------------------------------------------------
    // if multiple lists on this panel then go through each list and
    // call save on it. Once all these lists have been saved then notify the
    // caller of this function through it's returned Observable.
    // -----------------------------------------------------------------------------------
    return new Observable<any>(observer => {
      const savedArray: boolean[] = Array(pagePaneComponentArray.length).fill(false);

      pagePaneComponentArray.forEach((pagePaneComponent, index) => {
        pagePaneComponent.save().subscribe(saved => {
          if (!saved) {
            observer.next(saved);
          }

          savedArray[ index ] = saved;
          if (savedArray.filter(savedElement => savedElement).length === savedArray.length) {
            observer.next(saved);
          }
        });
      });
    });
  }

  public sendBusinessContext(): void {
    const pagePaneComponentArray = this.getPagePaneListComponents();
    if (pagePaneComponentArray.length === 0) {
      return; // safety
    } else if (pagePaneComponentArray.length === 1) {
      pagePaneComponentArray[0].sendBusinessContext();
    } else {
      const pagePane = pagePaneComponentArray.find(pane => pane.panel.panelType === 'MultiPanePanel');
      if (pagePane) {
        const paneSpec: LMMultiPagePaneSpec = pagePane.pane;
        const parentPane = this.getParentPane(paneSpec);
        if (parentPane) {
          parentPane.sendBusinessContext();
        }
      }
    }
  }

  // ---------------------------------------------------
  // EVENT HANDLERS
  // ---------------------------------------------------
  onPanelContentDisplayed(event: LMContentDisplayedEvent) {
    if (!this.isDashboardPanePanel()) {
      if (this.undisplayedPaneIdList && event.pane) {
        // filter out the completed paneId.
        this.undisplayedPaneIdList = this.undisplayedPaneIdList.filter(paneId => paneId !== event.pane.paneId);

        // if there are still undisplayed panels then return
        if (this.undisplayedPaneIdList.length > 0) {
          return;
        }
      }
    }

    this.panelContentDisplayed.emit(event);
  }

  onUpdateToolbarStateEvent() {
    this.updateToolbarStateEvent.emit();
  }

  public onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  onContentModified(event: any) {
    this.panelContentModified.emit(event);
  }

  onPaneContentModified(fieldChangedEvent: LMListFieldChangedEvent) {
    this.panelContentModified.emit(fieldChangedEvent);
  }

  onPanelDataUpdated(event: LMPanelTransactionEvent) {
    this.panelDataUpdated.emit(event);
  }

  onPaneListRowSelected(paneListSelectionEvent: LMPaneListSelectionEvent) {
    if (!this.isMultiPanePanel() || paneListSelectionEvent.listComponent === undefined) {
      return;
    }

    const paneSpec: any = paneListSelectionEvent.pagePaneListComponent.getPaneSpec();
    const selectionMode: SohoDataGridSelectable = paneListSelectionEvent.listComponent.displayType === 'chart' ?
      'single' : this.lmListSpecUtilService.getSelectionMode(paneListSelectionEvent.listComponent.listSpec);

    if (selectionMode !== 'single') {
      // if not single select then onListRowActivated will be messaged instead.
      return;
    }

    let fromDataView: any = null;

    if (paneListSelectionEvent.selectedRow.idx > -1) {
      const rowIndex: number = paneListSelectionEvent.selectedRow.idx;
      fromDataView = paneListSelectionEvent.listComponent.getDataView(rowIndex);
    }

    this.updateDependentPanes(paneSpec, fromDataView);
  }

  onPaneListRowActivated(panelRowActivatedEvent: LMPaneRowActivatedEvent) {
    if (!this.isMultiPanePanel()) {
      return;
    }

    const paneSpec: any = panelRowActivatedEvent.pagePaneListComponent.getPaneSpec();
    const selectionMode: SohoDataGridSelectable = this.lmListSpecUtilService.getSelectionMode(panelRowActivatedEvent.listComponent.listSpec);
    if (selectionMode !== 'mixed') {
      return;
    }

    let fromDataView: any;

    if (panelRowActivatedEvent.activatedEvent.row > -1) {
      const rowIndex: number = panelRowActivatedEvent.activatedEvent.row;
      fromDataView = panelRowActivatedEvent.listComponent.getDataView(rowIndex);
    } else {
      fromDataView = null;
    }

    this.updateDependentPanes(paneSpec, fromDataView);
  }

  onPaneListRowDeactivated(panelRowActivatedEvent: LMPaneRowActivatedEvent) {
    if (!this.isMultiPanePanel()) {
      return;
    }

    const paneSpec: any = panelRowActivatedEvent.pagePaneListComponent.getPaneSpec();
    const fromDataView: any = null;

    if (panelRowActivatedEvent.activatedEvent.row === -1) {
      this.updateDependentPanes(paneSpec, fromDataView);
    } else if (panelRowActivatedEvent.listComponent.listSpec.busListSpec.selection !== 'multiple') {
      return;
    }

    // todo: need to disable and clear related lists when selection is 'multiple' and onPaneListRowDeactivated - Phillip 5/9/17
  }

  onPaneListSendContextMessageEvent(paneListSendContextMessageEvent: LMPaneListSendContextMessageEvent) {
    if (this.panel && this.panel.panelType === 'Panel') {
      this.sendContextMessageEvent.emit(paneListSendContextMessageEvent.contextMsgs);
    } else if (this.panel && this.panel.panelType === 'MultiPanePanel') {
      const paneSpec: LMMultiPagePaneSpec = paneListSendContextMessageEvent.pagePaneListComponent.getPaneSpec();
      if (paneSpec.isRelativePanel) {
        const parentPane = this.getParentPane(paneSpec);
        if (parentPane) {
          parentPane.sendBusinessContext(paneListSendContextMessageEvent.contextMsgs);
        }
      } else {
        this.sendContextMessageEvent.emit(paneListSendContextMessageEvent.contextMsgs);
      }
    }
  }

  // ---------------------------------------------------
  // PRIVATE METHODS
  // ---------------------------------------------------
  private getPagePaneListComponents(request?: LMRefreshRequest): Array<any> {
    if (this.panel.panelType === 'Panel') {
      return this.pagePaneListComponents.toArray();
    }

    if (this.panel.panelType === 'MultiPanePanel') {
      return this.getOrderedPagePaneListComponents(request?.originatingSourceId);
    }

    if (this.panel.panelType === 'DashBoardPanel') {
      return this.pagePaneDashBoardComponents.toArray();
    }

    return [];
  }

  public isMultiPanePanel(): boolean {
    return (this.panel !== undefined && this.panel.panes !== undefined && this.panel.panes.length > 1);
  }

  public isSinglePanePanel(): boolean { // tslint:disable-line
    return (this.panel !== undefined && (this.panel.pane !== undefined || this.panel.panes.length === 1));
  }

  public isDashboardPanePanel(): boolean {
    return this.panel.panelType === 'DashBoardPanel';
  }

  public isMultiListPanel(): boolean {
    return this.panel?.panelType === 'MultiListPanel';
  }

  public get isMenuPanel(): boolean {
    if (this.isDashboardPanePanel()) {
      return false;
    }
    return (this.isSinglePanePanel() && (this.panel.pane && this.panel.pane.menuPanelLayout !== undefined)
      || (this.panel.panes && this.panel.panes.length === 1 && this.panel.panes[0].pane.menuPanelLayout !== undefined));
  }

  public get isSinglePaneURLPanel(): boolean {
    if (this.isDashboardPanePanel()) {
      return false;
    }
    return (this.isSinglePanePanel() && (this.panel.pane && this.panel.pane.paneType === 'URLPanel') ||
      (this.panel.panes && this.panel.panes.length === 1 && this.panel.panes[0].pane.paneType === 'URLPanel'));
  }

  public get singlePanelPane() {
    return this.panel.pane ? this.panel.pane : this.panel.panes[0].pane;
  }

  private buildDependentListMap(): void {
    this.pagePaneListComponents.forEach((item: LMPagePaneListComponent) => {
      this.pagePaneMap.set(item.getPaneSpec().paneId, item);
    });
  }

  private getDependentPagePaneListComponents(paneId: string): Array<LMPagePaneListComponent> {
    let depPaneIdList = [];

    const pagePane = this.pagePaneMap.get(paneId);
    if (pagePane && pagePane.getPaneSpec().pane.depPanes) {
      depPaneIdList = pagePane.getPaneSpec().pane.depPanes;
    }

    const depPaneArray = this.pagePaneListComponents.filter((pagePaneListComponent: LMPagePaneListComponent) =>
      (depPaneIdList.indexOf(pagePaneListComponent.getPaneSpec().paneId) > -1));

    return depPaneArray;
  }

  // tslint:disable-next-line:no-unused-variable - method use in template
  public getClasses(pane: any): string {
    let classes = this.lmPagePanelHelperService.getPagePaneClasses(this.panel, pane);
    if (this.insideTabs) {
      classes += ' in-page-tab';
    }
    classes += ' scrollable-y';
    return classes;
  }

  public getVerticalSplitWidth(loc: number, index: number) {
    const width = index === 0 ? loc : 100 - loc;
    return this.domSanitizer.bypassSecurityTrustStyle(`calc(${width}% - 30px)`);
  }

  private buildHTMLVariables() {
    this.multiPanePanel = this.isMultiPanePanel();
    this.dashboardPanePanel = this.isDashboardPanePanel();
    this.menuPanel = this.isMenuPanel;
    this.singleUrlPanePanel = this.isSinglePaneURLPanel;
    this.singlePanePanel = this.isSinglePanePanel();
    this.multiListPanel = this.isMultiListPanel();
  }

  private buildPanesHeightClasses() {
    if (this.multiPanePanel && !this.dashboardPanePanel && !this.menuPanel && !this.singleUrlPanePanel && !this.multiListPanel) {
      // this.orderedUIPanes = JSON.parse(JSON.stringify(this.orderedPanes));
      this.orderedPanes.forEach((pane: LMMultiPagePaneSpec, index: number) => {
        this.orderedUIPanes.push({
          paneSpec: pane,
          classes: this.getClasses(pane),
          verticalSplitWidth: (this.isVerticalSplit ? this.getVerticalSplitWidth(this.panel.loc, index) : '')
        });
      });
    }
  }

  private updateDependentPanes(paneSpec: any, fromDataView: LMDataView) {
    const dependentPaneArray: Array<LMPagePaneListComponent> = this.getDependentPagePaneListComponents(paneSpec.paneId);
    if (dependentPaneArray.length <= 0) {
      return; // noop
    }
    for (const dependentPane of dependentPaneArray) {
      dependentPane.fromDataView = fromDataView;
    }
  }

  private getParentPane(paneSpec: any): LMPagePaneListComponent {
    const pagePane = this.pagePaneMap.get(paneSpec.paneId);
    if (pagePane) {
      for (const panelPane of pagePane.panel.panes) {
        if (panelPane.hasDependentPanels && panelPane.pane.depPanes.length > 0) {
          const pane = panelPane.pane;
          for (const depPane of pane.depPanes) {
            if (depPane === paneSpec.paneId) {
              return this.pagePaneMap.get(panelPane.paneId);
            }
          }
        }
      }
    }

    return null;
  }

  private getOrderedPagePaneListComponents(excludeListId?: string): Array<LMPagePaneListComponent> {
    const orderedListArray: Array<LMPagePaneListComponent> = new Array(0);

    if (this.panel.panelType === 'MultiPanePanel') {
      this.pagePaneListComponents.forEach((item: LMPagePaneListComponent) => {
        if (item.getPaneSpec().hasDependentPanels) {
          this.processDependentPanels(item, orderedListArray);
        }

        if (!orderedListArray.includes(item)) {
          orderedListArray.push(item);
        }
      });
    }

    if (excludeListId) {
      return orderedListArray.filter(pagePanelList => excludeListId !== pagePanelList.lmListComponent.listId);
    } else {
      return orderedListArray;
    }
  }

  private processDependentPanels(pagePaneListComponent: LMPagePaneListComponent, orderedListArray: Array<LMPagePaneListComponent>): void {
    const dependentPaneArray: Array<LMPagePaneListComponent> = this.getDependentPagePaneListComponents(pagePaneListComponent.getPaneSpec().paneId);
    for (const item of dependentPaneArray) {
      if (item.getPaneSpec().hasDependentPanels) {
        this.processDependentPanels(item, orderedListArray);
      }

      if (!orderedListArray.includes(item)) {
        orderedListArray.push(item);
      }
    }
  }

  savePanelState() {
    this.pagePaneListComponents.toArray().forEach(pane => pane.savePaneState());
  }

  private checkIfVerticalSplit() {
    if (this.panel.noOfPanels === 2) {
      const panes = this.panel.panes.filter((pane) => pane.paneName === 'Pane1' || pane.paneName === 'Pane3');

      this.isVerticalSplit = panes.length === 2 && this.panel.loc !== undefined;
    }
  }
}
